import { removeUserSaveSession } from "../component/context/auth";
import { SignalRTransport } from "./transports/SignalRTransport";
import { ServerEvents, ClientMethods, ConnectionState } from "./types/events";

/**
 * RealtimeService (Central Hub & Gateway)
 * Manages active transport, event routing, typed actions, and reconnection.
 * Designed with modern singleton & adapter patterns.
 */
class RealtimeService {
    constructor(transport = null) {
        this.transport = transport || new SignalRTransport();
        this.currentUser = null;
        this.isInitialized = false;
        this._listeners = new Map();
        this._stateSubscribed = false;
        this._forceLogoutSubscribed = false;
    }

    /**
     * Swap transport at runtime (e.g. to WebSocketTransport / uWebSockets)
     * @param {IRealtimeTransport} newTransport
     */
    async setTransport(newTransport) {
        if (this.transport) {
            await this.transport.disconnect();
        }
        this.transport = newTransport;
        if (this.isInitialized) {
            await this.init();
        }
    }

    /**
     * Initialize the realtime connection
     * @param {Object} config
     */
    async init(config = {}) {
        if (this.isInitialized && this.transport.getState() === ConnectionState.CONNECTED) {
            return;
        }
        this.isInitialized = true;
        try {
            // Subscribe to force logout from admin
            if (!this._forceLogoutSubscribed) {
                this._forceLogoutSubscribed = true;
                this.on(ServerEvents.FORCE_LOGOUT, (payload) => {
                    console.warn("[RealtimeService] Force logout received:", payload);
                    removeUserSaveSession();
                    if (typeof window !== "undefined") {
                        const reasonMsg = payload?.message || "تم تسجيل خروجك من قبل مسؤول النظام";
                        alert(reasonMsg);
                        window.location.href = "/Login";
                    }
                });
            }

            // Subscribe to state changes for automatic reconnection handling
            if (!this._stateSubscribed) {
                this._stateSubscribed = true;
                this.onConnectionStateChange(async (newState) => {
                    if (newState === ConnectionState.CONNECTED && this.currentUser) {
                        try {
                            await this.registerUser(this.currentUser);
                            const actualUser = this.currentUser.user || this.currentUser;
                            const userId = actualUser.Id || actualUser.id;
                            if (userId && typeof window !== "undefined") {
                                const pageTitle = document.title ? document.title.split('|')[0].trim() : window.location.pathname;
                                const pageLocation = window.location.pathname + window.location.search;
                                await this.setPageTitle(userId, pageTitle, pageLocation);
                            }
                        } catch (e) {
                            console.error("[RealtimeService] Re-registration error on reconnect:", e);
                        }
                    }
                });
            }

            await this.transport.connect(config);
            if (this.currentUser) {
                await this.registerUser(this.currentUser);
                this.setupActivityTracker();
            }
        } catch (err) {
            console.error("[RealtimeService] Initialization error:", err);
        }
    }

    /**
     * Setup client idle/active detection tracker
     */
    setupActivityTracker() {
        if (typeof window === "undefined" || this._activityTrackerSetup) return;
        this._activityTrackerSetup = true;

        let isIdle = false;
        let idleTimer = null;
        const IDLE_TIMEOUT_MS = 120000; // 2 minutes of inactivity marks user as idle

        const setIdleState = (idle) => {
            if (isIdle !== idle) {
                isIdle = idle;
                this.setUserActivity(idle).catch(() => {});
            }
        };

        const resetTimer = () => {
            if (isIdle) {
                setIdleState(false);
            }
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
                setIdleState(true);
            }, IDLE_TIMEOUT_MS);
        };

        const activityEvents = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
        activityEvents.forEach(evt => {
            window.addEventListener(evt, resetTimer, { passive: true });
        });

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                setIdleState(true);
            } else {
                resetTimer();
            }
        });

        resetTimer();
    }

    async setUserActivity(isIdle) {
        if (this.transport.getState() !== ConnectionState.CONNECTED) return;
        return await this.transport.invoke(ClientMethods.SET_USER_ACTIVITY, isIdle);
    }

    /**
     * Disconnect connection
     */
    async disconnect() {
        if (this.currentUser) {
            await this.userLogout(this.currentUser);
        }
        await this.transport.disconnect();
        this.isInitialized = false;
    }

    // ==========================================
    // Event Subscriptions (Listeners)
    // ==========================================

    on(eventName, handler) {
        this.transport.on(eventName, handler);
        return () => this.transport.off(eventName, handler);
    }

    off(eventName, handler) {
        this.transport.off(eventName, handler);
    }

    onOnlineUsers(handler) {
        return this.on(ServerEvents.ONLINE_USERS, handler);
    }

    onNotification(handler) {
        return this.on(ServerEvents.SEND_NOTIFICATION, handler);
    }

    onUpdatePage(handler) {
        return this.on(ServerEvents.SEND_UPDATE_PAGE, handler);
    }

    onFlashMessage(handler) {
        return this.on(ServerEvents.RECEIVE_FLASH_MESSAGE, handler);
    }

    onAnnouncement(handler) {
        return this.on(ServerEvents.RECEIVE_ANNOUNCEMENT, handler);
    }

    onRemoteRedirect(handler) {
        return this.on(ServerEvents.RECEIVE_REMOTE_REDIRECT, handler);
    }

    onGhostSessionsPurged(handler) {
        return this.on(ServerEvents.GHOST_SESSIONS_PURGED, handler);
    }

    onConnectionStateChange(handler) {
        if (typeof this.transport.onStateChange === "function") {
            return this.transport.onStateChange(handler);
        }
        return () => { };
    }

    getConnectionId() {
        return this.transport.getConnectionId();
    }

    getConnectionState() {
        return this.transport.getState();
    }

    // ==========================================
    // Outgoing Client Methods (Actions)
    // ==========================================

    async registerUser(user) {
        this.currentUser = user;
        if (!user) return;
        const actualUser = user.user || user;
        const userId = actualUser.Id || actualUser.id;
        if (!userId) return;

        const fullName = actualUser.Name || [actualUser.FirstName, actualUser.SecondName, actualUser.LastName].filter(Boolean).join(" ").trim() || actualUser.Username || "";
        const currentPageTitle = typeof document !== "undefined" && document.title ? document.title.split('|')[0].trim() : "";
        const currentLocation = typeof window !== "undefined" ? (window.location.pathname + window.location.search) : "";

        return await this.transport.invoke(ClientMethods.REGISTER_USER, {
            Id: userId,
            Username: actualUser.Username || actualUser.username || "",
            Name: fullName,
            Email: actualUser.Email || actualUser.email || "",
            Phone: actualUser.Phone || actualUser.phone || "",
            Sex: actualUser.Sex || actualUser.sex || "",
            Country: actualUser.Country || actualUser.country || "",
            Birthday: actualUser.Birthday || actualUser.birthday || "",
            ActivateUntilDate: actualUser.ActivateUntilDate || actualUser.activateUntilDate || "",
            Photo: actualUser.Photo || actualUser.photo || null,
            Title: currentPageTitle,
            Location: currentLocation,
            UserTypeId: actualUser.UserType ? actualUser.UserType.Id : (actualUser.UserTypeId || null),
            UserType: actualUser.UserType || null,
            SchoolId: actualUser.School ? actualUser.School.Id : (actualUser.SchoolId || null),
            School: actualUser.School || null,
            IsTeacher: actualUser.IsTeacher || false
        });
    }

    async getOnlineUsers() {
        return await this.transport.invoke(ClientMethods.GET_ONLINE_USERS);
    }

    async userLogout(user) {
        const actualUser = user?.user || user;
        const userId = actualUser?.Id || actualUser?.id || actualUser;
        if (userId && typeof userId === "number") {
            await this.transport.invoke(ClientMethods.USER_LOGOUT, userId);
        }
        this.currentUser = null;
    }

    async forceLogoutUser(userId, reason = "admin_action") {
        const id = typeof userId === "object" ? (userId?.Id || userId?.id || userId?.user?.Id) : userId;
        if (!id) return;
        return await this.transport.invoke(ClientMethods.FORCE_LOGOUT_USER, id, reason);
    }

    async setPageTitle(userId, title, location) {
        const id = typeof userId === "object" ? (userId?.Id || userId?.id || userId?.user?.Id) : userId;
        if (!id) return;
        return await this.transport.invoke(ClientMethods.SET_PAGE_TITLE, {
            UserId: id,
            Title: title || "",
            Location: location || ""
        });
    }

    async sendEmail(title, message, users) {
        return await this.transport.invoke(ClientMethods.SEND_EMAIL, {
            Title: title,
            Message: message,
            Users: users
        });
    }

    async shareFile(studentIds, file) {
        const ids = Array.isArray(studentIds) ? studentIds : [studentIds];
        return await this.transport.invoke(ClientMethods.SHARE_FILE, {
            StudentId: ids,
            Data: file
        });
    }

    async shareLesson(studentIds, lesson, indexType) {
        const ids = Array.isArray(studentIds) ? studentIds : [studentIds];
        return await this.transport.invoke(ClientMethods.SHARE_LESSON, {
            StudentId: ids,
            Data: lesson,
            IndexType: indexType
        });
    }

    async shareMission(studentIds, mission) {
        const ids = Array.isArray(studentIds) ? studentIds : [studentIds];
        return await this.transport.invoke(ClientMethods.SHARE_MISSION, {
            StudentId: ids,
            Data: mission
        });
    }

    async notificationIsView(notificationType, userId) {
        return await this.transport.invoke(ClientMethods.NOTIFICATION_IS_VIEW, notificationType, userId);
    }

    async sendContactUs(title) {
        return await this.transport.invoke(ClientMethods.SEND_CONTACT_US, title);
    }

    async schoolRegister(title) {
        return await this.transport.invoke(ClientMethods.SCHOOL_REGISTER, title);
    }

    async errorReport() {
        return await this.transport.invoke(ClientMethods.ERROR_REPORT);
    }

    async errorReportIsSolve(errorReport, targetUserId) {
        const userId = targetUserId || (errorReport?.User?.Id);
        return await this.transport.invoke(ClientMethods.ERROR_REPORT_IS_SOLVE, errorReport, userId);
    }

    async updateGameScore(userId) {
        return await this.transport.invoke(ClientMethods.UPDATE_GAME_SCORE, userId);
    }

    async sendFlashMessage(userId, title, message, type = "info") {
        const id = typeof userId === "object" ? (userId?.Id || userId?.id || userId?.user?.Id) : userId;
        if (!id || !message) return;
        return await this.transport.invoke(ClientMethods.SEND_FLASH_MESSAGE, id, title || "تنبيه إداري", message, type || "info");
    }

    async sendFlashMessageToPage(pageUrl, title, message, type = "info") {
        if (!pageUrl || !message) return;
        return await this.transport.invoke(ClientMethods.SEND_FLASH_MESSAGE_TO_PAGE, pageUrl, title || "تنبيه خاص برواد الصفحة", message, type || "info");
    }

    async broadcastAnnouncement(title, message, type = "info", duration = 12) {
        if (!message) return;
        return await this.transport.invoke(ClientMethods.BROADCAST_ANNOUNCEMENT, title || "إعلان عام", message, type || "info", duration || 12);
    }

    async redirectUser(userId, targetUrl, reason = "") {
        const id = typeof userId === "object" ? (userId?.Id || userId?.id || userId?.user?.Id) : userId;
        if (!id || !targetUrl) return;
        return await this.transport.invoke(ClientMethods.REDIRECT_USER, id, targetUrl, reason);
    }

    async purgeGhostSessions() {
        return await this.transport.invoke(ClientMethods.PURGE_GHOST_SESSIONS);
    }
}

// Export singleton instance
export const realtimeService = new RealtimeService();
export default realtimeService;
